/**
 * api.ts — the ONE API client (build/10 §8).
 *
 * No raw `fetch` anywhere else in the console. This is the single place that
 * injects the correlation id, parses the contract's error envelope, and (from
 * Phase 4) authenticates the request and refreshes on 401.
 *
 * ### THE SESSION IS A COOKIE, SO THIS FILE NO LONGER CARRIES A CREDENTIAL
 *
 * It used to read an access token out of `sessionStorage` and set
 * `Authorization: Bearer`. Both tokens now live in `httpOnly` cookies the
 * browser attaches itself (todo §5), which is why there is no `Authorization`
 * header below and nothing here that could be made to leak one. What replaces it
 * is two lines: `credentials` on every request, and the CSRF echo on every
 * write — because a credential the browser attaches automatically is one another
 * site can make it attach.
 *
 * `session-store` imports `AuthUser` back from here, but only as a TYPE, so the
 * cycle is erased at compile time and there is no runtime import loop.
 */
import { csrfToken, session } from './session-store';

/**
 * In production the client is a static bundle served from nafis.warsha.ai and
 * calls the API on its own hostname; in dev, Vite proxies /api to :4000.
 */
const API_BASE = import.meta.env.VITE_API_URL ?? '';

/**
 * `same-origin`, which is also the default — stated because the session now
 * depends on it and a default is not a decision.
 *
 * Not `include`: the cookies are `SameSite=Strict` and host-only, so they are
 * not sent cross-site whatever this says, and asking for `include` would imply a
 * cross-origin deployment that this design deliberately does not support. The
 * console reaches the API through nginx's same-origin `/api` proxy precisely so
 * the session cookie never has to be `SameSite=None` (see `client/nginx.conf`).
 * Pointing `VITE_API_URL` at another hostname would break the cookie session,
 * not merely the CORS preflight.
 */
const CREDENTIALS: RequestCredentials = 'same-origin';

/** Methods that can change state, and therefore need the CSRF echo. */
const UNSAFE_METHODS = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

/**
 * The CSRF header, on writes only.
 *
 * The server refuses a cookie-authenticated write without it, which is the point
 * — a form on another site can make the browser send the cookie but cannot make
 * it send this. Read from the cookie at call time rather than cached: it must
 * not outlive a sign-out. Absent (signed out, or a deployment that never set
 * one) means the header is omitted, and the server answers with a code that says
 * so rather than a generic 403.
 */
function csrfHeader(method: string | undefined): Record<string, string> {
  if (!UNSAFE_METHODS.has((method ?? 'GET').toUpperCase())) return {};
  const token = csrfToken();
  return token ? { 'X-CSRF-Token': token } : {};
}

export interface ApiErrorShape {
  code: string;
  message: string;
  traceId: string;
  details?: unknown;
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly payload: ApiErrorShape,
  ) {
    super(payload.message);
    this.name = 'ApiError';
  }
  /** Quote this to support — it ties the click to logs, audit, and webhooks. */
  get traceId() {
    return this.payload.traceId;
  }
}

function newTraceId(): string {
  return globalThis.crypto?.randomUUID?.() ?? `trace-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

/**
 * Refresh the session, at most once at a time.
 *
 * The single-flight promise matters: a screen that fires four queries on mount
 * gets four simultaneous 401s, and four parallel refreshes would each rotate the
 * token — three of them presenting one that the first had already consumed,
 * which the server correctly treats as replay and responds to by revoking every
 * session. Refreshing on a shared promise turns that into one exchange.
 *
 * **It only covers THIS tab, and that is why the server gained a grace window.**
 * The refresh token used to live in `sessionStorage`, which is per-tab, so two
 * tabs held two independent sessions and could never collide. They now share one
 * cookie jar, so two tabs that expire together present the same token and one
 * loses the race — a cross-context problem no promise in this module can see.
 * `services/auth.service.ts` forgives a token consumed by rotation moments ago;
 * this promise still removes the common case, which is four queries from one
 * screen.
 *
 * There is no `refreshToken` in the body any more. The browser attaches the
 * `httpOnly` refresh cookie, which is the only way it can be sent — and the
 * reason no script on the page can steal it.
 */
let inFlightRefresh: Promise<boolean> | null = null;

async function refreshSession(): Promise<boolean> {
  // Nothing to refresh if the console does not believe it has a session. Without
  // this, every 401 on a signed-out console would fire a pointless POST.
  if (!session.user()) return false;

  inFlightRefresh ??= (async () => {
    try {
      const res = await fetch(`${API_BASE}/api/auth/refresh`, {
        method: 'POST',
        credentials: CREDENTIALS,
        headers: { 'Content-Type': 'application/json', ...csrfHeader('POST') },
      });
      if (!res.ok) {
        session.clear();
        return false;
      }
      // The new cookies arrived with the response. All this reads is the hint —
      // and refreshing it is how a role change reaches the console without a
      // sign-out.
      const body = (await res.json()) as { user?: AuthUser };
      if (body?.user?.email) session.setUser(body.user);
      return true;
    } catch {
      return false;
    } finally {
      inFlightRefresh = null;
    }
  })();

  return inFlightRefresh;
}

export async function apiFetch<T>(path: string, init: RequestInit = {}, retrying = false): Promise<T> {
  const traceId = newTraceId();

  const res = await fetch(`${API_BASE}${path}`, {
    ...init,
    credentials: CREDENTIALS,
    headers: {
      'Content-Type': 'application/json',
      'X-Trace-Id': traceId,
      ...csrfHeader(init.method),
      ...init.headers,
    },
  });

  /**
   * Silent refresh on 401, then one retry.
   *
   * Only once — `retrying` guards it. A refresh that succeeds and is still
   * answered with 401 means the problem is not the token, and retrying forever
   * would hammer the API on every rendered screen.
   *
   * Never for `/auth/*`: a failed login answers 401 and must surface as bad
   * credentials, not as an expired session.
   */
  if (res.status === 401 && !retrying && !path.startsWith('/api/auth/')) {
    if (await refreshSession()) return apiFetch<T>(path, init, true);
    session.clear();
  }

  /**
   * A 204 is a success with nothing to parse.
   *
   * It used to fall through to the non-JSON branch below and throw, which every
   * caller of a 204 route then had to swallow — and `signOut` swallowing an
   * `ApiError` is how a logout that genuinely failed would have looked exactly
   * like one that worked. Now that the server-side revocation IS the sign-out,
   * the console needs to be able to tell.
   */
  if (res.status === 204 || res.status === 205) return undefined as T;

  const isJson = res.headers.get('content-type')?.includes('application/json');
  const body = isJson ? await res.json().catch(() => null) : null;

  if (!res.ok) {
    throw new ApiError(res.status, {
      code: body?.error?.code ?? 'UNKNOWN',
      message: body?.error?.message ?? `Request failed with ${res.status}`,
      traceId: body?.error?.traceId ?? traceId,
      details: body?.error?.details,
    });
  }

  /**
   * A 2xx that is not JSON never came from this API, so treat it as a failure
   * rather than a value.
   *
   * This is not defensive padding — it is the bug that hid a broken deployment.
   * The SPA's nginx fallback was answering /api/* with index.html and HTTP 200,
   * so every caller here received `null` typed as its response type, took no
   * error branch, and rendered an empty card. "The API returned nothing" and
   * "the request never reached the API" looked identical from every screen.
   */
  if (!isJson) {
    throw new ApiError(res.status, {
      code: 'NON_JSON_RESPONSE',
      message:
        `Expected JSON from ${path} but received '${res.headers.get('content-type') ?? 'no content-type'}'. ` +
        'The request probably did not reach the API.',
      traceId,
    });
  }

  return body as T;
}

/**
 * A non-JSON GET — the CSV export, and nothing else so far.
 *
 * It reuses `apiFetch`'s auth and single-flight refresh by going through the
 * same code path up to the point where that function insists on JSON. A raw
 * `fetch` here would be a second place that has to know about tokens, which is
 * exactly what `10 §8` has one client to avoid.
 *
 * **RETURNS BYTES, NOT A STRING, AND THAT IS THE WHOLE POINT.**
 *
 * `csv.ts` writes a UTF-8 BOM on every export precisely so Excel does not read
 * an Arabic bank as the system codepage — and this function used to call
 * `res.text()`, which per the Fetch spec performs a "UTF-8 decode" and
 * **strips the BOM**. The bytes left the server correct, the browser removed
 * the one thing that made them readable, and the file the authoring team opened
 * showed every Arabic prompt as `Ø£Ø­Ø§ÙˆÙ„`.
 *
 * A downloaded file has no reason to be decoded into a JS string and re-encoded
 * on its way to a Blob. Passing the bytes through untouched cannot lose
 * anything, which is why this returns a `Blob` and `downloadFile` takes one.
 */
export async function apiBlob(path: string, retrying = false): Promise<Blob> {
  const traceId = newTraceId();

  const res = await fetch(`${API_BASE}${path}`, {
    credentials: CREDENTIALS,
    headers: { 'X-Trace-Id': traceId },
  });

  if (res.status === 401 && !retrying) {
    if (await refreshSession()) return apiBlob(path, true);
    session.clear();
  }

  if (!res.ok) {
    const body = await res.json().catch(() => null);
    throw new ApiError(res.status, {
      code: body?.error?.code ?? 'UNKNOWN',
      message: body?.error?.message ?? `Request failed with ${res.status}`,
      traceId: body?.error?.traceId ?? traceId,
      details: body?.error?.details,
    });
  }

  return res.blob();
}

/**
 * Hand a generated file to the browser.
 *
 * An `<a download>` over a blob rather than navigating to the URL. The original
 * reason was that the export needed an `Authorization` header and a navigation
 * cannot carry one; a cookie session removes that constraint — the browser would
 * attach it to a navigation quite happily. The approach stays, for the reason
 * `apiBlob` exists: a navigation hands the response to the browser's download
 * machinery, and this path is the one that can be *asserted* about, byte for
 * byte, in a test. A file this console builds itself (the catalogue bundle) has
 * no URL to navigate to at all.
 */
export function downloadFile(filename: string, contents: string | Blob, mime: string) {
  // A Blob passes through untouched. A string is content this console BUILT
  // (the catalogue bundle's JSON), so there is nothing to preserve — but a file
  // that came off the API must never be decoded and re-encoded on the way here.
  // See `apiBlob`: `res.text()` strips the BOM that makes an Arabic CSV
  // readable in Excel.
  const blob = contents instanceof Blob ? contents : new Blob([contents], { type: mime });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
}

export interface HealthResponse {
  ok: boolean;
  service: string;
  /** Which deployment answered. Absent from an API older than S1. */
  environment?: 'development' | 'staging' | 'production';
  /**
   * Who authenticates operators on this deployment. Absent from an API that
   * predates the identity seam — which the login screen has to tolerate, because
   * the whole point of asking is that it does not know yet.
   */
  auth?: { mode: 'local' | 'oidc'; issuer: string | null; passwordSignIn: boolean };
  db: { connected: boolean; replicaSet: string | null; transactionsAvailable: boolean };
  plugins: string[];
}

/** One row of the instance feed — the progress view, never the items. */
export interface AssessmentSummary {
  assessmentId: string;
  candidateRef: string;
  moduleId: string;
  personaId: string;
  administeredLocale: string;
  state: 'ready' | 'in_progress' | 'submitted' | 'scoring' | 'completed' | 'expired' | 'flagged';
  itemCount: number;
  answeredCount: number;
  progress: number;
  scoringConfigVersion: number;
  planVersion: string;
  createdAt: string;
  expiresAt: string;
}

export interface ResultSummaryRow {
  resultId: string;
  instanceId: string;
  candidateRef: string;
  moduleId: string;
  personaId: string;
  configVersion: number;
  instrumentVersion: string;
  qcFlags: string[];
  supersedes: string | null;
  finalizedAt: string;
  resultUrl: string;
}

export interface Paged<T> {
  items: T[];
  page: number;
  pageSize: number;
  total: number;
}

const qs = (params: Record<string, string | number | undefined>) => {
  const search = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) if (v !== undefined && v !== '') search.set(k, String(v));
  const s = search.toString();
  return s ? `?${s}` : '';
};

/**
 * The console's role and capability vocabulary, mirrored from
 * `server/src/contract/auth.ts`.
 *
 * MIRRORED, NOT IMPORTED, and that is a build constraint rather than a
 * preference: the client image copies `server/src/contract` without a
 * `server/node_modules`, so `zod` cannot resolve from those files and production
 * client code cannot import `@contract` (see `client/tsconfig.build.json`).
 * `src/test/strings.test.ts` mirrors the closed registries for the same reason.
 *
 * **The VALUES are not mirrored — only the types.** Which role holds which
 * capability comes from `GET /api/auth/capabilities`, so the table the console
 * renders and the rule the server enforces are the same object. A capability map
 * written out here would be the drift this whole workstream exists to remove.
 */
export type Role = 'admin' | 'psychometrician' | 'analyst';

export type Capability =
  | 'items:author'
  | 'design:draft'
  | 'catalogue:publish'
  | 'assessments:run'
  | 'qc:release'
  | 'platform:manage';

export interface CapabilityMatrix {
  capabilities: Capability[];
  roles: Record<Role, Capability[]>;
}

export interface AuthUser {
  userId: string;
  email: string;
  role: Role;
  name: string;
}

/** The languages the console has a translation catalogue for (`lib/strings/`). */
export type ConsoleLocale = 'en-AE' | 'ar-AE';

/**
 * `AuthUser` plus what is read once per session rather than carried in the token.
 *
 * `locale` is an account property, so it follows an operator between machines —
 * unlike the theme, which is deliberately device-local. It is not in the access
 * token on purpose: claims stay minimal, and a language change that only took
 * effect after the next refresh would read as the setting being broken.
 */
export interface OperatorProfile extends AuthUser {
  locale: ConsoleLocale;
}

export interface ConsoleUser {
  userId: string;
  email: string;
  role: Role;
  name: string;
  active: boolean;
  createdAt: string;
  lastLoginAt?: string | null;
}

/**
 * What `POST /auth/login` and `POST /auth/refresh` answer with — and what they
 * deliberately do NOT.
 *
 * This was `TokenPair`, carrying `accessToken` and `refreshToken`. Both are now
 * `httpOnly` cookies set by the same response, so nothing on the page can read
 * either, and the type is what says so: a field that does not exist cannot be
 * put in `sessionStorage` by the next person to touch this file.
 *
 * `expiresIn` survives because it is a *fact about* the session rather than the
 * session itself — a client may use it to refresh early instead of waiting for a
 * 401. `user` survives for the same reason: it is what the console renders.
 */
export interface SessionResponse {
  /** Seconds until the access cookie's token expires. */
  expiresIn: number;
  user: AuthUser;
}

/* -------------------------------------------- integration consumers (G2) */

/**
 * The `/v1` scope vocabulary, mirrored from `server/src/contract/webhook.ts`.
 *
 * Mirrored for the same build reason as `Role` above — the contract is not
 * importable from the client bundle. This one is a CLOSED set (`z.enum`), so
 * `strings.test.ts` asserts the console has a label for every member; a scope
 * added server-side must not reach an operator as a raw wire string.
 */
export type Scope = 'assessments:write' | 'results:read' | 'reference:read';

/**
 * A key as it can be READ — which is to say, without the key.
 *
 * Only a hash is stored, so nothing on this shape can be turned back into a
 * credential. `IssuedKey` is the one and only shape that carries a plaintext.
 */
export interface ApiKeyRow {
  keyId: string;
  label: string;
  scopes: Scope[];
  active: boolean;
  createdAt: string | null;
  rotatedAt: string | null;
}

export interface ConsumerRow {
  consumerId: string;
  name: string;
  webhookUrl: string | null;
  /** Whether a signing secret is SET — never what it is. */
  webhookConfigured: boolean;
  active: boolean;
  createdAt: string;
  apiKeys: ApiKeyRow[];
}

/**
 * The 201 from issue and rotate — the only response in the platform's life that
 * contains a plaintext key.
 *
 * It is not stored, not re-readable and not recoverable: an operator who loses
 * it rotates. `notice` is the server saying so; the console shows its own
 * localised sentence and does not rely on that English reaching an Arabic
 * reader.
 */
export interface IssuedKey {
  consumerId: string;
  keyId: string;
  label: string;
  scopes: Scope[];
  active: boolean;
  createdAt?: string;
  /** `nafis_<keyId>.<secret>`. Shown once, never again. */
  apiKey: string;
  notice: string;
  /** Rotation only — the keyId whose secret stopped working immediately. */
  replaced?: string;
}

export const api = {
  health: () => apiFetch<HealthResponse>('/api/health'),

  /**
   * Sign in. The credentials come back as cookies, not as a value.
   *
   * There is deliberately no `X-Auth-Response: token` here — that header is how
   * a *script* asks for tokens in the body, and a browser asking for it would
   * undo the whole change.
   */
  login: (email: string, password: string) =>
    apiFetch<SessionResponse>('/api/auth/login', { method: 'POST', body: JSON.stringify({ email, password }) }),

  me: () => apiFetch<OperatorProfile>('/api/auth/me'),

  /**
   * What each role may do — served from the same map the server's gates resolve
   * against, so the Role-permissions table cannot describe a policy the API does
   * not enforce. Read once per session; `useCan` is the consumer.
   */
  capabilities: () => apiFetch<CapabilityMatrix>('/api/auth/capabilities'),

  /**
   * Set your OWN display language. There is no route to set anyone else's, and
   * `updateUser` deliberately has no `locale` field — how a person reads the
   * console is not a permission an administrator grants.
   */
  setLocale: (locale: ConsoleLocale) =>
    apiFetch<OperatorProfile>('/api/auth/me', {
      method: 'PATCH', body: JSON.stringify({ locale }),
    }),

  users: () => apiFetch<Paged<ConsoleUser>>('/api/users?pageSize=100'),
  createUser: (body: { email: string; role: Role; name?: string }) =>
    apiFetch<ConsoleUser & { generatedPassword?: string }>('/api/users', {
      method: 'POST', body: JSON.stringify(body),
    }),
  updateUser: (userId: string, patch: { role?: Role; active?: boolean }) =>
    apiFetch<ConsoleUser>(`/api/users/${userId}`, { method: 'PATCH', body: JSON.stringify(patch) }),
  changePassword: (currentPassword: string, newPassword: string) =>
    apiFetch<void>('/api/auth/password', {
      method: 'POST', body: JSON.stringify({ currentPassword, newPassword }),
    }),

  /**
   * End the session — SERVER-SIDE, which is the only place it can be ended.
   *
   * No argument: the refresh token is an `httpOnly` cookie the console cannot
   * read, and the server revokes whichever one the browser presents. That
   * revocation is the sign-out; the `Set-Cookie` clearing that comes back with
   * it is housekeeping, and a browser that ignored it would still hold a dead
   * credential.
   */
  logout: () => apiFetch<void>('/api/auth/logout', { method: 'POST' }),

  /* ------------------------------------------ integration consumers (G2) */

  /**
   * The consumer registry — admin only, on the CONSOLE surface.
   *
   * A consumer cannot manage itself: a credential able to widen its own scopes
   * or mint a sibling would make the scope map decorative. Issuance is a console
   * action taken by a person, which is why these live under `/api` and not `/v1`.
   */
  consumers: () => apiFetch<Paged<ConsumerRow>>('/api/consumers?pageSize=100'),

  createConsumer: (body: {
    name: string; webhookUrl?: string; webhookSecret?: string; active?: boolean;
  }) => apiFetch<ConsumerRow>('/api/consumers', { method: 'POST', body: JSON.stringify(body) }),

  updateConsumer: (
    consumerId: string,
    patch: { name?: string; webhookUrl?: string; webhookSecret?: string; active?: boolean },
  ) => apiFetch<ConsumerRow>(`/api/consumers/${consumerId}`, {
    method: 'PATCH', body: JSON.stringify(patch),
  }),

  /** 201 with the plaintext. There is no second call that can return it again. */
  issueKey: (consumerId: string, body: { label: string; scopes: Scope[] }) =>
    apiFetch<IssuedKey>(`/api/consumers/${consumerId}/keys`, {
      method: 'POST', body: JSON.stringify(body),
    }),

  /** Mints a replacement and kills the old secret immediately — no grace window. */
  rotateKey: (consumerId: string, keyId: string) =>
    apiFetch<IssuedKey>(`/api/consumers/${consumerId}/keys/${keyId}/rotate`, { method: 'POST' }),

  /**
   * `DELETE` on the route, `active:false` in the document.
   *
   * Soft-retire, like every other entity here: "which key made that call"
   * outlives the key, so the record stays and only the credential stops working.
   */
  revokeKey: (consumerId: string, keyId: string) =>
    apiFetch<ConsumerRow>(`/api/consumers/${consumerId}/keys/${keyId}`, { method: 'DELETE' }),

  assessments: (params: { candidateRef?: string; moduleId?: string; state?: string; page?: number; pageSize?: number } = {}) =>
    apiFetch<Paged<AssessmentSummary>>(`/api/assessments${qs(params)}`),

  results: (params: { candidateRef?: string; moduleId?: string; page?: number; pageSize?: number } = {}) =>
    apiFetch<Paged<ResultSummaryRow>>(`/api/results${qs(params)}`),

  /**
   * One candidate's dossier (W1) — the plan they were pinned to, marked off
   * against what they have actually sat. Not the same as filtering the instance
   * feed by `candidateRef`: that can only show modules they have STARTED.
   */
  candidate: (candidateRef: string) =>
    apiFetch<CandidateDossier>(`/api/candidates/${encodeURIComponent(candidateRef)}`),

  /* ------------------------------------------------------------- catalogue */

  modules: () => apiFetch<Paged<ModuleRow>>('/api/modules?pageSize=100'),
  module: (moduleId: string) => apiFetch<ModuleDetail>(`/api/modules/${moduleId}`),
  personas: () => apiFetch<Paged<PersonaRow>>('/api/personas'),

  questions: (params: {
    moduleId?: string; itemType?: string; status?: string; tag?: string; q?: string;
    page?: number; pageSize?: number;
  } = {}) => apiFetch<Paged<QuestionRow>>(`/api/questions${qs(params)}`),

  matrix: () => apiFetch<Paged<MatrixCellRow>>('/api/matrix'),
  questionStats: () => apiFetch<{ items: ItemTypeStat[] }>('/api/questions/stats'),
  coverage: () => apiFetch<{ items: CellCoverage[] }>('/api/matrix/coverage'),

  /* ------------------------------------------------------------- authoring */

  createQuestion: (item: unknown) =>
    apiFetch<QuestionRow>('/api/questions', { method: 'POST', body: JSON.stringify(item) }),
  updateQuestion: (itemId: string, item: unknown) =>
    apiFetch<QuestionRow>(`/api/questions/${itemId}`, { method: 'PUT', body: JSON.stringify(item) }),
  /**
   * The five lifecycle transitions (`plan-beta` B2).
   *
   * The two that REMOVE an item from the certified pool answer with what that
   * did to the matrix; the three that cannot shrink a pool answer with the item
   * alone. That asymmetry is the server's, and it is deliberate — see
   * `leavingThePool` in routes/authoring.ts.
   */
  reviewQuestion: (itemId: string) =>
    apiFetch<QuestionRow>(`/api/questions/${itemId}/review`, { method: 'POST' }),
  certifyQuestion: (itemId: string) =>
    apiFetch<QuestionRow>(`/api/questions/${itemId}/certify`, { method: 'POST' }),
  uncertifyQuestion: (itemId: string) =>
    apiFetch<LifecycleImpact>(`/api/questions/${itemId}/uncertify`, { method: 'POST' }),
  retireQuestion: (itemId: string) =>
    apiFetch<LifecycleImpact>(`/api/questions/${itemId}/retire`, { method: 'POST' }),
  unretireQuestion: (itemId: string) =>
    apiFetch<QuestionRow>(`/api/questions/${itemId}/unretire`, { method: 'POST' }),
  importQuestions: (items: unknown[], dryRun = true) =>
    apiFetch<ImportReport>('/api/questions/import', {
      method: 'POST',
      body: JSON.stringify({ items, dryRun }),
    }),

  /* --------------------------------------------- the round trip (T2 / B3–B4b) */

  /**
   * Export takes the SAME filters as the list, so "export what I am looking at"
   * is one call with the screen's own params.
   */
  /**
   * Which matrix cells this item currently qualifies for (`plan-beta` B6).
   *
   * The feedback loop between tagging and coverage, answered from the item's
   * side — coverage has always answered it from the cell's.
   */
  itemCells: (itemId: string) =>
    apiFetch<{
      itemId: string;
      moduleId: string;
      certified: boolean;
      cells: Array<{
        cellId: string; personaId: string; moduleId: string;
        itemCount: number; pinned: boolean; excluded: boolean;
      }>;
      excludedFrom: Array<{ cellId: string; personaId: string }>;
    }>(`/api/questions/${itemId}/cells`),

  /**
   * ONE request for a whole selection (`plan-beta` B5).
   *
   * Not a loop of single-item calls: the per-row report is the point, and the
   * server answers coverage once for the batch rather than once per item.
   */
  bulkTransition: (itemIds: string[], op: 'review' | 'certify' | 'uncertify' | 'retire' | 'unretire') =>
    apiFetch<BulkReport>('/api/questions/bulk/transition', {
      method: 'POST', body: JSON.stringify({ itemIds, op }),
    }),

  bulkMeta: (body: {
    itemIds: string[];
    addTags?: string[];
    removeTags?: string[];
    setAudienceTags?: string[];
    setContextTags?: string[];
    readingLevel?: number | null;
  }) =>
    apiFetch<Omit<BulkReport, 'impact'> & { coverage: CellCoverage[] }>(
      '/api/questions/bulk/meta',
      { method: 'POST', body: JSON.stringify(body) },
    ),

  exportQuestions: (params: {
    moduleId?: string; itemType?: string; status?: string; tag?: string; q?: string; ids?: string;
  } = {}) => apiBlob(`/api/questions/export${qs({ ...params, format: 'csv' })}`),

  importQuestionsCsv: (csv: string, dryRun = true, columnMap?: Record<string, string>) =>
    apiFetch<ImportReport>('/api/questions/import', {
      method: 'POST',
      body: JSON.stringify({ csv, dryRun, ...(columnMap ? { columnMap } : {}) }),
    }),

  /* ------------------------------------------------- the change log (S3) */

  /** Read-only by construction — there is no write route to call. */
  audit: (params: {
    category?: string; severity?: string; actor?: string; event?: string;
    entityId?: string; from?: string; to?: string; page?: number; pageSize?: number;
  } = {}) => apiFetch<Paged<AuditRow>>(`/api/audit${qs(params)}`),

  verifyAudit: () => apiFetch<AuditVerification>('/api/audit/verify'),

  exportCatalogue: (includeItems: boolean) =>
    apiFetch<CatalogueBundle>(`/api/catalogue/export${qs({ includeItems: String(includeItems) })}`),

  importCatalogue: (bundle: unknown, dryRun = true) =>
    apiFetch<CatalogueImportReport>('/api/catalogue/import', {
      method: 'POST',
      body: JSON.stringify({ bundle, dryRun }),
    }),

  /* ------------------------------------------- modules: create and upgrade (T5) */

  /** The live scoring registry — what a module may bind, read from the plugins. */
  plugins: () => apiFetch<{ items: PluginDescriptor[] }>('/api/plugins'),

  /**
   * Re-score a cohort under the current config and plugin build (A3 / J2).
   *
   * **`dryRun` defaults to true on the SERVER and is passed explicitly here
   * anyway.** Writing a cohort must be asked for in words, and a client that
   * relied on a default to avoid writing 400 results is a client one refactor
   * away from writing them.
   */
  batchRescore: (filter: BatchRescoreFilter) =>
    apiFetch<BatchRescoreSummary>('/api/assessments/rescore/batch', {
      method: 'POST',
      body: JSON.stringify({ dryRun: true, ...filter }),
    }),

  createModule: (module: unknown) =>
    apiFetch<ModuleDetail>('/api/modules', { method: 'POST', body: JSON.stringify(module) }),

  updateModule: (moduleId: string, patch: unknown) =>
    apiFetch<ModuleDetail>(`/api/modules/${moduleId}`, { method: 'PUT', body: JSON.stringify(patch) }),

  /**
   * What a `updateModule` with this body WOULD do — writes nothing.
   *
   * Sent with the same body as the save so the two cannot disagree, and callable
   * with `{}` to ask "what is stranded in this module right now?".
   */
  previewModule: (moduleId: string, patch: unknown) =>
    apiFetch<ModuleChangePreview>(`/api/modules/${moduleId}/preview`, {
      method: 'POST',
      body: JSON.stringify(patch),
    }),

  /**
   * The three module transitions (`plan-beta` §0b).
   *
   * `publish` answers with the coverage it is publishing into. The two that
   * switch a module OFF answer with what that costs — and only `retire` can be
   * refused, because only `retire` cannot be undone.
   */
  publishModule: (moduleId: string) =>
    apiFetch<{ module: ModuleDetail; readiness: ModuleReadiness }>(
      `/api/modules/${moduleId}/publish`, { method: 'POST' },
    ),
  unpublishModule: (moduleId: string) =>
    apiFetch<ModuleSwitchOff>(`/api/modules/${moduleId}/unpublish`, { method: 'POST' }),
  retireModule: (moduleId: string) =>
    apiFetch<ModuleSwitchOff>(`/api/modules/${moduleId}/retire`, { method: 'POST' }),

  /* ------------------------------------------------ QC review (contract v0 §5) */

  /**
   * Results whose score is being withheld pending review.
   *
   * Its own endpoint rather than a filter on the results list, because the two
   * answer different questions — that list is "what has been scored", this is
   * "what is waiting for a human". Readable by both roles: an analyst cannot
   * release, but "how much is held" is a bank-health question.
   */
  qcQueue: () => apiFetch<{ items: QcQueueRow[]; total: number }>('/api/results/qc-queue'),

  /**
   * Release a held result — `flagged → completed`, `qc:release` only.
   *
   * This is the transition that stops the score being withheld from `/v1`, and
   * contract v0 fires `result.finalized` from it rather than from scoring.
   *
   * **`reason` is REQUIRED and the server refuses without it** (Workstream V2).
   * It used to post no body at all; `QcDecisionBody` now validates one, so a
   * bodyless call is a 400 `VALIDATION_ERROR`. The floor is mirrored in
   * `lib/qc.ts` and checked against the contract by a test.
   */
  releaseResult: (resultId: string, reason: string) =>
    apiFetch<QcDecision>(`/api/results/${resultId}/release`, {
      method: 'POST', body: JSON.stringify({ reason }),
    }),

  /**
   * Put a released result back on hold — `completed → flagged`.
   *
   * The UNDO of a release and nothing wider: a run that completed on its own
   * merits was never released, so there is no decision to reverse and the server
   * answers `NOT_RELEASED`. Same capability as release, deliberately — the power
   * to put a score back under review is the same power as letting it out.
   */
  holdResult: (resultId: string, reason: string) =>
    apiFetch<QcDecision>(`/api/results/${resultId}/hold`, {
      method: 'POST', body: JSON.stringify({ reason }),
    }),

  /**
   * The evidence behind one hold (Workstream V1) — the response pattern in
   * delivery order, the per-item latency, and the statistic each detector fired
   * on.
   *
   * RESULT-scoped, not run-scoped, because a run can carry several results whose
   * flags differ after a re-score, and the thresholds shown are read from the
   * config version the named result records. Needs no capability: seeing the
   * evidence and taking the decision are different acts, and only the second is
   * `qc:release`.
   */
  qcEvidence: (resultId: string) =>
    apiFetch<QcEvidence>(`/api/results/${resultId}/qc-evidence`),

  /* ------------------------------------------- personas: CRUD and plans (T4) */

  persona: (personaId: string) => apiFetch<PersonaDetail>(`/api/personas/${personaId}`),

  createPersona: (persona: unknown) =>
    apiFetch<PersonaDetail>('/api/personas', { method: 'POST', body: JSON.stringify(persona) }),

  /**
   * Identity fields apply immediately; `draftPlanTemplate` writes the DRAFT.
   *
   * There is no call here that edits a published plan, and that is the API's
   * shape rather than this client's restraint — `planTemplate` and `planVersion`
   * are not in the server's update schema at all. A plan reaches candidates only
   * through `publishPersona`.
   */
  updatePersona: (
    personaId: string,
    patch: {
      name?: Localized; segment?: string; defaultLocale?: string;
      draftPlanTemplate?: PlanEntry[];
    },
  ) => apiFetch<PersonaDetail>(`/api/personas/${personaId}`, {
    method: 'PUT',
    body: JSON.stringify(patch),
  }),

  /** Writes nothing. Meaningful with no draft too: "what is stranded right now?". */
  previewPersona: (personaId: string) =>
    apiFetch<PersonaPublishPreview>(`/api/personas/${personaId}/preview`, { method: 'POST' }),

  publishPersona: (personaId: string) =>
    apiFetch<{ persona: PersonaDetail; published: PersonaPublishPreview }>(
      `/api/personas/${personaId}/publish`, { method: 'POST' },
    ),

  discardPersonaDraft: (personaId: string) =>
    apiFetch<PersonaDetail>(`/api/personas/${personaId}/discard`, { method: 'POST' }),

  /** Reversible, unlike a module retire — `active` is a boolean, not a third state. */
  retirePersona: (personaId: string) =>
    apiFetch<PersonaRetireImpact>(`/api/personas/${personaId}/retire`, { method: 'POST' }),

  reactivatePersona: (personaId: string) =>
    apiFetch<PersonaDetail>(`/api/personas/${personaId}/reactivate`, { method: 'POST' }),

  /* --------------------------------------------------------- the pipeline */

  startAssessment: (body: { candidateRef: string; moduleId: string; personaId: string; locale?: string }) =>
    apiFetch<AssessmentSummary>('/api/assessments', { method: 'POST', body: JSON.stringify(body) }),

  assessment: (assessmentId: string) =>
    apiFetch<AssessmentSummary>(`/api/assessments/${assessmentId}`),

  assessmentItems: (assessmentId: string) =>
    apiFetch<{ assessmentId: string; page: number; totalPages: number; items: DeliveryItemDto[] }>(
      `/api/assessments/${assessmentId}/items?pageSize=60`,
    ),

  saveResponses: (assessmentId: string, responses: ResponseDto[]) =>
    apiFetch<{ saved: number; progress: number; state: string }>(
      `/api/assessments/${assessmentId}/responses`,
      { method: 'POST', body: JSON.stringify({ responses }) },
    ),

  submitAssessment: (assessmentId: string, responses: ResponseDto[]) =>
    apiFetch<{ resultId: string; resultUrl: string; state: string }>(
      `/api/assessments/${assessmentId}/submit`,
      { method: 'POST', body: JSON.stringify({ responses }) },
    ),

  result: (resultId: string) => apiFetch<ResultEnvelopeDto>(`/api/results/${resultId}`),

  aggregate: (params: { moduleId?: string; personaId?: string; from?: string; to?: string } = {}) =>
    apiFetch<DashboardAggregate>(`/api/results/aggregate${qs(params)}`),

  /**
   * Per-item statistics for one module (Workstream M1).
   *
   * Nested under the module because that is the unit an item analysis is
   * computed over — p-values from two modules share no scale and belong on no
   * single screen.
   *
   * `includeHeld` is sent as an explicit string rather than being left to
   * default. The server reads it with `BooleanQuery` precisely because
   * `Boolean('false')` is `true`, so the value a caller sends to turn something
   * OFF is the one a coerced parser would ignore.
   */
  itemAnalysis: (
    moduleId: string,
    params: { personaId?: string; from?: string; to?: string; includeHeld?: boolean } = {},
  ) =>
    apiFetch<ItemAnalysis>(
      `/api/modules/${moduleId}/item-analysis${qs({
        personaId: params.personaId,
        from: params.from,
        to: params.to,
        includeHeld: params.includeHeld === undefined ? undefined : String(params.includeHeld),
      })}`,
    ),

  /** Result-scoped: the envelope carries no instanceId, so the server resolves it. */
  rescoreResult: (resultId: string) =>
    apiFetch<ResultEnvelopeDto>(`/api/results/${resultId}/rescore`, { method: 'POST' }),

  saveMatrixCell: (cell: unknown) =>
    apiFetch<{ cell: MatrixCellRow; coverage: CellCoverage }>('/api/matrix/cell', {
      method: 'PUT',
      body: JSON.stringify(cell),
    }),
};

/* ----------------------------------------------------------------- shapes */

/** One row of a bulk report — `ok: false` always carries a reason. */
export interface BulkOutcome {
  itemId: string;
  ok: boolean;
  from?: string;
  to?: string;
  code?: string;
  message?: string;
}

export interface BulkReport {
  requested: number;
  applied: number;
  outcomes: BulkOutcome[];
  /** Only a transition that SHRINKS a pool can move coverage; otherwise empty. */
  impact: { cells: CellCoverage[]; newlyWarning: CellCoverage[] };
}

export interface Localized { en: string; ar: string }

export interface ModuleRow {
  moduleId: string;
  name: Localized;
  description: Localized;
  modelType: string;
  status: 'draft' | 'active' | 'retired';
  estimatedMinutes: number;
  questionCount: number;
  certifiedCount: number;
  scoringConfigVersion: number;
  pluginVersion: string;
  supportedItemTypes: string[];
  /**
   * How many personas this module has a norm table for (Workstream J).
   *
   * `0` means uncalibrated — every result it produces reports a band and no
   * percentile, which is accurate and is also something an operator can now fix.
   * Optional because an API older than J does not send it, and the Scoring
   * screen must degrade to saying nothing rather than to claiming zero.
   */
  normGroupCount?: number;
}

export interface ScoringConfig {
  modelType: string;
  version: number;
  dimensions: string[];
  dimensionLabels: Record<string, string>;
  bands: Array<{ min: number; max: number; label: string }>;
  minCompletion: number;
  qcThresholds: { straightLineThreshold: number; fastMs: number };
  options: Record<string, unknown>;
  norms?: Record<string, Record<string, { mean: number; sd: number }>>;
}

export interface ModuleDetail extends Omit<ModuleRow, 'questionCount' | 'certifiedCount' | 'scoringConfigVersion'> {
  pluginId: string;
  supportedItemTypes: string[];
  scoringConfig: ScoringConfig;
  /** SUPERSEDED configs, oldest first — never the current one (Workstream A1). */
  scoringConfigHistory: ScoringConfig[];
}

/** One registered scoring model, as the create wizard needs it (T5). */
export interface PluginDescriptor {
  modelType: string;
  displayName: string;
  version: string;
  /** Derived server-side; `Module.pluginId` is a label, not a resolved reference. */
  pluginId: string;
  supportedItemTypes: string[];
  /** Every build still resolvable for a pinned run, oldest first (A2). */
  retainedVersions: string[];
  /** `null` when the model offers no starting shape — say so, do not invent one. */
  configTemplate: ScoringConfig | null;
  /**
   * What a structured editor may change about this model's config (T5).
   *
   * `null` when the plugin does not describe its config surface, and the editor
   * then offers the raw-JSON tab alone. That is a real state, not a degraded
   * one: JSON plus the server's `validateConfig` round-trip is what every model
   * had before this existed, and a form guessed from a config's current shape
   * would be a form asserting rules nobody declared.
   *
   * Mirrors `ScoringConfigFields` in `server/src/contract/scoringConfig.ts` BY
   * HAND — production client code cannot import `@contract` (no `zod` in the
   * client image; see `ResultEnvelopeDto`). `src/test/scoring-config.test.ts`
   * parses a fixture of this shape through the contract schema and fails the
   * build if the two drift.
   */
  configFields: ScoringConfigFields | null;
}

/* ------------------------------------------------- batch re-score (A3 / J2) */

/**
 * What a cohort re-score would do, or did.
 *
 * Mirrors `BatchRescoreSummary` in `server/src/services/rescore.service.ts`. The
 * endpoint has existed since PROGRESS §15 and had **no console UI at all** until
 * Workstream J — the plan's checkbox was truthful about the endpoint and silent
 * about the entry point, which is how the most scoring-shaped operation in the
 * platform went a workstream unreachable.
 */
export interface BatchRescoreRow {
  assessmentId: string;
  /** Compares the SCORE, not the envelope — ids and timestamps differ by construction. */
  outcome: 'changed' | 'unchanged' | 'failed';
  previousResultId?: string;
  newResultId?: string;
  configVersion?: { from: number; to: number };
  instrumentVersion?: { from: string; to: string };
  headline?: { from: string; to: string };
  /** Largest movement first; empty when only the headline moved. */
  dimensionDeltas?: Array<{ key: string; from: number; to: number; delta: number }>;
  /**
   * Present ONLY when the re-score moves this run into or out of a QC hold.
   *
   * *"412 results would change"* is a decision; *"412 would change and 12 would
   * go on hold"* is a different one, and it is the one an operator needs before
   * writing anything.
   */
  qcState?: { from: string; to: 'completed' | 'flagged' };
  error?: string;
}

export interface BatchRescoreSummary {
  dryRun: boolean;
  /** Instances the filter matched, before the cap. */
  matched: number;
  /** Instances actually examined. Lower than `matched` means the cap bit. */
  examined: number;
  /** True when `matched > examined` — stated by the server, never silent. */
  truncated: boolean;
  changed: number;
  unchanged: number;
  failed: number;
  held: number;
  unheld: number;
  rows: BatchRescoreRow[];
}

export interface BatchRescoreFilter {
  moduleId?: string;
  personaId?: string;
  from?: string;
  to?: string;
  dryRun?: boolean;
  limit?: number;
}

/** One model-specific knob inside `ScoringConfig.options`. */
export interface ConfigOptionSpec {
  key: string;
  /** English, and the editor's SECOND choice after `config.option.<key>`. */
  label: string;
  kind: 'number' | 'boolean' | 'select';
  choices?: string[];
  min?: number;
  max?: number;
  step?: number;
  help?: string;
}

export interface ScoringConfigFields {
  /** `editable: false` ⇒ the set is the model; only labels may change. */
  dimensions: { editable: boolean; fixed?: string[] };
  /** `labels: null` ⇒ free text; an array ⇒ the only labels that will save. */
  bands: { labels: string[] | null };
  /**
   * Whether `score()` reads `config.norms`. False hides the norms editor
   * entirely — authoring a table a model ignores produces no percentile and no
   * explanation, which is the failure the flag exists to prevent.
   */
  norms: { supported: boolean };
  options: ConfigOptionSpec[];
}

/** An item a pending module change would leave un-scoreable. */
export interface StrandedItem {
  itemId: string;
  itemType: string;
  status: 'draft' | 'review' | 'certified' | 'retired';
  reason: 'type_unsupported' | 'dimension_dropped';
  detail: string;
}

export interface ModuleReadiness {
  moduleId: string;
  itemsChecked: number;
  stranded: StrandedItem[];
  strandedCertified: number;
  coverage: CellCoverage[];
  newlyWarning: CellCoverage[];
}

/**
 * What a module edit would do, before it does it (`plan-beta` §6).
 *
 * `pinnedToCurrent.open` is the number that turns a save into a decision: a
 * version bump with runs behind it is a different act from one without, and
 * nothing in the request body says which you are making.
 */
export interface ModuleChangePreview {
  moduleId: string;
  fields: string[];
  configDiff: Array<{ field: string; from: unknown; to: unknown; scoringSemantic: boolean }>;
  versionBump: { from: number; to: number; bumps: boolean };
  pinnedToCurrent: { open: number; completed: number };
  readiness: ModuleReadiness;
}

/** The answer to unpublish and retire — what switching a module off costs. */
export interface ModuleSwitchOff {
  module: ModuleDetail;
  impact: {
    /** Persona plans that name it. Blocking for retire; reported for unpublish. */
    personaPlans: string[];
    openInstances: number;
    /** Always true, and stated: an open run froze its items and pinned its config. */
    openInstancesUnaffected: boolean;
  };
}

export interface QuestionRow {
  itemId: string;
  moduleId: string;
  itemType: string;
  prompt: Localized;
  status: 'draft' | 'review' | 'certified' | 'retired';
  version: number;
  dimension?: string;
  skillId?: string;
  reverseKeyed?: boolean;
  scaleMin?: number;
  scaleMax?: number;
  options?: Array<{ optionId: string; label: Localized; isCorrect?: boolean; value?: number; anchorLevel?: number; dimension?: string }>;
  /** Scale labels. Delivered to the candidate — they are not scoring metadata. */
  anchors?: { en: string[]; ar: string[] };
  meta: { tags: string[]; audienceTags: string[]; contextTags: string[]; readingLevel?: number; sensitivityFlags: string[]; taxonomyRefs: string[] };
}

/**
 * The answer to a transition that shrinks a module's certified pool.
 *
 * `newlyWarning` is the one an operator has to see: a cell that was green before
 * this click and is amber after it will refuse to generate for that persona, and
 * the item they just corrected is why.
 */
export interface LifecycleImpact {
  item: QuestionRow;
  impact: {
    cells: CellCoverage[];
    newlyWarning: CellCoverage[];
    /** Cells that asked for this item by name in `include`. */
    pinnedBy: string[];
  };
}

/**
 * The last QC decision taken on a run — `QcReviewRecord` in `contract/result.ts`.
 *
 * The HISTORY of decisions is the audit trail, not this. It answers only "where
 * does this stand now, and who put it there".
 */
export interface QcReviewRecord {
  decision: 'released' | 'held';
  reason: string;
  /** The operator id, as `emit()` records it on the matching audit row. */
  actor: string;
  at: string;
}

/** What both QC decision routes answer with. */
export interface QcDecision {
  resultId: string;
  assessmentId: string;
  state: string;
  qcReview: QcReviewRecord;
}

/** One result held for quality-control review. */
export interface QcQueueRow {
  resultId: string;
  assessmentId: string;
  candidateRef: string;
  moduleId: string;
  personaId: string;
  /** What the plugin raised. Empty would mean the hold has no reason, which cannot happen. */
  qcFlags: string[];
  scoredAt: string | null;
  configVersion: number;
  /**
   * **Present only when a REVIEWER put this run back** (V2).
   *
   * A machine-flagged run that no human has decided about carries no record, so
   * the ABSENCE of this field is the signal: it is what separates "the
   * heuristics caught this" from "a person released it and then changed their
   * mind". The queue renders the two differently for exactly that reason — they
   * are different jobs for whoever works it next.
   */
  qcReview?: QcReviewRecord;
}

export interface PlanEntry {
  moduleId: string;
  order: number;
  required: boolean;
  itemCount: number;
  variant?: string;
}

/** One module's row in a candidate's plan. Mirrors `ModuleStatus` in the contract. */
export interface DossierModule {
  moduleId: string;
  state: string;
  progress?: number | null;
  assessmentId?: string | null;
  startedAt?: string | null;
  completedAt?: string | null;
  expiresAt?: string | null;
  resultId?: string | null;
  configVersion?: number | null;
  administeredLocale?: string | null;
}

export interface DossierResult {
  resultId: string;
  moduleId: string;
  configVersion: number;
  qcFlags: string[];
  supersedes: string | null;
  finalizedAt: string;
}

export interface CandidateDossier {
  candidateRef: string;
  /** `registration` — a /v1 context. `runs` — derived from their instances. */
  resolvedFrom: 'registration' | 'runs' | null;
  personaId: string | null;
  personaName: Localized | null;
  planVersion: string | null;
  /** False when the pinned plan version could not be resolved from history. */
  planResolvable: boolean;
  registeredAt: string | null;
  overall: {
    requiredModules: number;
    completed: number;
    inProgress: number;
    pending: number;
    percentComplete: number;
  };
  modules: DossierModule[];
  results: DossierResult[];
}

export interface PersonaRow {
  personaId: string;
  name: Localized;
  segment: string;
  defaultLocale: string;
  planVersion: string;
  active: boolean;
  moduleCount: number;
  planTemplate: PlanEntry[];
  /** T4. Computed server-side — an EMPTY draft is still a draft. */
  hasDraft: boolean;
  draftModuleCount: number | null;
  retainedVersions: number;
}

/** One persona in full: the published plan, the pending draft, and every superseded version. */
export interface PersonaDetail extends Omit<PersonaRow, 'moduleCount' | 'draftModuleCount'> {
  draftPlanTemplate?: PlanEntry[];
  planTemplateHistory: Array<{ planVersion: string; planTemplate: PlanEntry[]; supersededAt: string }>;
  published: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export interface PlanEntryDiff {
  moduleId: string;
  change: 'added' | 'removed' | 'changed' | 'unchanged';
  detail: string[];
}

/**
 * What publishing a draft would change and cost — the dry run publish itself
 * gates on, so the dialog and the gate can never disagree.
 */
export interface PersonaPublishPreview {
  personaId: string;
  hasDraft: boolean;
  currentPlanVersion: string;
  nextPlanVersion: string | null;
  diff: PlanEntryDiff[];
  coverage: Array<{
    moduleId: string;
    cellExists: boolean;
    itemCount: number;
    effectivePoolSize: number;
    status: 'ok' | 'warning' | 'no_cell';
  }>;
  pinnedRuns: { open: number; completed: number };
  blockers: Array<{ code: string; message: string }>;
  warnings: Array<{ code: string; message: string }>;
}

export interface PersonaRetireImpact {
  persona: PersonaDetail;
  impact: { matrixCells: number; openInstances: number; completedInstances: number };
}

export interface MatrixCellRow {
  cellId: string;
  personaId: string;
  moduleId: string;
  itemCount: number;
  pool: { query: Record<string, unknown> };
  include: string[];
  exclude: string[];
  balancing: { byDimension: boolean };
  shuffle: boolean;
}

export interface ItemTypeStat {
  itemType: string;
  total: number;
  certified: number;
  draft: number;
  retired: number;
}

export interface CellCoverage {
  personaId: string;
  moduleId: string;
  itemCount: number;
  effectivePoolSize: number;
  status: 'ok' | 'warning';
  dimensionBalance: Array<{ dimension: string; available: number; needed: number; ok: boolean }>;
}

export interface DeliveryItemDto {
  itemId: string;
  position: number;
  itemType: string;
  prompt: Localized;
  options?: Array<{ optionId: string; label: Localized }>;
  scale?: { min: number; max: number } | null;
  savedResponse?: number | string | string[] | null;
}

export interface ResponseDto {
  itemId: string;
  value: number | string | string[];
  responseTimeMs?: number;
}

/**
 * One occupation a profile aligns with — `EscoRef + fitScore` in
 * `contract/result.ts`.
 *
 * `fitScore` is **0..1, not a percentage**. Anything rendering it as one is
 * converting, and must say so at the call site.
 */
export interface RecommendedPathway {
  escoUri: string;
  label: Localized;
  fitScore: number;
}

/**
 * The stored envelope, as the console reads it.
 *
 * Typed loosely on purpose: `score` is a discriminated union whose members grow
 * with each model, and the console renders the render-agnostic `display`
 * projection rather than any typed member. Narrowing here would reintroduce the
 * model-awareness `visualization` and `display` exist to remove.
 *
 * ### The two fields that are typed, and the four that deliberately are not (N2)
 *
 * The route returns the STORED envelope verbatim, so a score block arrives with
 * everything its plugin put on it. This type used to declare `{type, display}`
 * and nothing else, which did not narrow the payload — it erased it, and the
 * console dropped two modelled fields on the floor because nothing said they
 * were there.
 *
 * `narrativeKeys` and `recommendedPathways` are declared because the screen
 * renders them, and both are **optional**: a score block that carries neither is
 * the normal case, and the sections key off PRESENCE rather than off which model
 * produced the block. That is the whole extension story — a fourth plugin that
 * emits `narrativeKeys` lights the section up with no edit here.
 *
 * `traits`, `hollandCode` and `gapAnalysis` are on the wire too and are
 * deliberately left untyped. Each is the shape of ONE model, and a console
 * that can name `score.traits.openness` is one refactor away from branching on
 * the model that has traits — the exact thing `display` exists to prevent.
 * Typing them is a decision to be taken when something renders them, not a
 * documentation exercise. (`gapAnalysis` is also *absent* rather than empty when
 * contract fix C2 is unresolved, so its absence carries meaning of its own.)
 *
 * **`skills` came off that list in N4**, under that file's own rule: something
 * renders it now. Only the two fields the technical read draws are declared —
 * `label`, `theta` and `standardError` — and the entry is not narrowed beyond
 * them, so this stays a statement about a field the console reads rather than a
 * copy of one model's block.
 *
 * ### The mirrored contract (N3, N4, N5)
 *
 * `display` mirrors `DisplayProjection` in `server/src/contract/result.ts` BY
 * HAND, for the reason `lib/qc.ts` sets out at length: the client image copies
 * `server/src/contract` without a `server/node_modules`, so production client
 * code importing `@contract` cannot resolve `zod` and the container build fails.
 * The guard therefore lives in a test — `src/test/norms.test.tsx` parses a
 * fixture shaped like this type through the contract's own `DisplayProjection`
 * and fails the build the moment the two drift.
 */
export interface ResultEnvelopeDto {
  resultId: string;
  candidateRef: string;
  moduleId: string;
  personaId: string;
  configVersion: number;
  instrumentVersion: string;
  escoEdition: string;
  administeredLocale: string;
  qcFlags: string[];
  supersedes: string | null;
  finalizedAt: string;
  visualization: 'radar' | 'bars' | 'gauge' | 'table' | 'profile' | 'hexagon';
  score: {
    type: string;
    display: {
      headline: {
        /**
         * The English sentence, unchanged — contract v0 publishes it and an
         * integrator stores it verbatim. It is also the console's SECOND choice
         * (`features/result/headline.tsx`): a phrase a human wrote beats a key.
         */
        value: string;
        label: Localized;
        /** N5 — a stable template id the console resolves against its catalogue. */
        key?: string;
        /**
         * N5 — interpolations for `key`. **KEYS, NEVER PROSE**: a dimension key
         * goes through `useDimensionLabel` and a band through the band lookup
         * before it reaches the sentence, or an Arabic report reads
         * "openness highest".
         */
        vars?: Record<string, string>;
      };
      /**
       * N3 — WHICH reference group produced the percentiles below: a persona id,
       * resolved for display by `usePersonaName`. Present only when at least one
       * dimension actually got one, so it can never name a table that produced
       * nothing.
       */
      normGroup?: string;
      dimensions: Array<{
        key: string;
        label: Localized;
        value: number;
        band: string;
        /**
         * N3 — 0..100, where this value sits against `normGroup`. **Absent when
         * no norm covers the dimension** (decision D-PP2), and absence renders
         * as nothing at all — not a dash, not a zero. A missing norm is not a
         * finding about the candidate.
         */
        percentile?: number;
        /**
         * N4 — an interval around `value`, in the SAME DISPLAY UNITS as `value`.
         *
         * This is the only interval anything may draw on a bar. It is NOT
         * `skills[].standardError`, which is SE(θ) on the logit scale and
         * describes a different estimator entirely.
         *
         * **It was called `confidence` and is not**, which is the point of the
         * rename: "confidence interval" is a coverage claim, and a reader who
         * assumes the usual 95% over-reads a ±1 SE band by a factor of two. The
         * schema cannot state a coverage level, so the field names its
         * CONSTRUCTION instead and coverage stays unclaimed — because it is.
         *
         * `basis` is a closed enum in the contract with one member today, and is
         * typed here as a plain string ON PURPOSE, the way `qcFlags` is: a member
         * added server-side must degrade to naming no basis, never print
         * `standard_error` at a reader. `features/result/viz.tsx` does that.
         */
        interval?: { low: number; high: number; basis: string };
      }>;
    };
    /**
     * Interpretation-band keys (`conscientiousness.high`), NOT prose. They are
     * resolved against a wording bank; `features/result/interpretation.tsx`
     * records what stands in for that bank until one exists.
     */
    narrativeKeys?: string[];
    recommendedPathways?: RecommendedPathway[];
    /**
     * HOW DEFINED THE PROFILE IS — highest dimension minus lowest, 0–100.
     *
     * Declared under this file's own rule for the untyped-on-purpose fields:
     * *"Typing them is a decision to be taken when something renders them"*.
     * Something does now — `ProfileShapeCard` in `features/result/
     * interpretation.tsx` — and it renders on PRESENCE, never on which model
     * emitted it. A second model that reports how spread its dimensions are
     * lights the same card up with no edit here.
     *
     * Optional because it is: results finalised before the field existed carry
     * no value, and the card renders nothing rather than a zero.
     */
    differentiation?: number;
    /**
     * HOW COHERENT THE TOP TWO DIMENSIONS ARE, where the model orders its
     * dimensions cyclically and adjacency means something.
     *
     * A closed set server-side. Typed as those three members here rather than
     * as `string` because the card maps each to a reviewed bilingual phrase and
     * a fourth member would need one; `qcFlags` is `string[]` for the opposite
     * reason — it is *displayed* through a lookup that can degrade.
     */
    consistency?: 'high' | 'medium' | 'low';
    /**
     * N4 — the θ estimates and their standard errors, for the TECHNICAL read.
     *
     * `standardError` is SE(θ) on the **logit scale**, and that is why it is
     * declared here rather than folded into a dimension: nothing may draw it on
     * a 0–100 bar. `display.dimensions[].interval` is the drawable one;
     * these two numbers are a fact about the precision of a different estimator,
     * and the screen that shows them says so.
     */
    skills?: Array<{
      label: Localized;
      theta?: number;
      standardError?: number;
    }>;
  };
}

/**
 * The spread of one dimension across a cohort — `DimensionDistribution` in
 * `contract/analysis.ts` (`plan-preproduction` M2).
 *
 * **Everything below `sufficient` is absent, never zero.** An SD over three
 * results and an SD over three hundred are different claims, so below the floor
 * the server withholds the derived numbers and reports only `n`. A console that
 * rendered `sd: 0` there would be inventing precision the platform refused to
 * claim — so every field here is optional and the screens branch on
 * `sufficient` rather than on `?? 0`.
 */
export interface DimensionDistribution {
  moduleId: string;
  key: string;
  /**
   * CANDIDATES, not results — one per (candidate, module), so a run re-scored
   * four times counts once. This is deliberately NOT the sibling `n` beside
   * `average`, which counts every result including superseded ones; the gap
   * between them is a real signal and the console surfaces it.
   */
  n: number;
  sufficient: boolean;
  mean?: number;
  /** SAMPLE standard deviation (n−1). Undefined at n = 1, which is honest. */
  sd?: number;
  min?: number;
  max?: number;
  /** Ten fixed-width bins over the 0–100 display scale, empty ones included. */
  deciles?: number[];
}

export interface DashboardAggregate {
  results: number;
  candidates: number;
  byModule: Array<{ moduleId: string; results: number }>;
  byDimension: Array<{
    moduleId: string;
    key: string;
    average: number;
    n: number;
    /**
     * Optional on the wire only because an API older than M2 does not send it —
     * the current server always does. Screens must tolerate its absence rather
     * than white-screening a stack mid-deploy.
     */
    distribution?: DimensionDistribution;
  }>;
  /** Completions per ISO week, oldest first — the trend line (`plan-beta` E). */
  byWeek: Array<{ week: string; completions: number }>;
  byHeadline: Array<{ moduleId: string; headline: string; count: number }>;
  qcFlags: Array<{ flag: string; count: number; rate: number }>;
  instances: {
    total: number;
    byState: Record<string, number>;
    byModule: Array<{ moduleId: string; state: string; count: number }>;
  };
}

/* ------------------------------------------ M1: item analysis (analysis.ts) */

/**
 * WHICH central-tendency statistic a row carries — `ItemStatistic` in the
 * contract, and the reason `central` is an object rather than a number.
 *
 * A `p_value` (the proportion who answered an mcq correctly) and a
 * `mean_response` (the mean mark on a likert's own scale) are not the same
 * measurement and must never appear under one heading. The server refused to
 * flatten them; the screen names each one in its own cell.
 */
export type ItemStatistic = 'p_value' | 'mean_response' | 'mean_option_value' | 'mean_anchor_level';

export interface ItemCentralTendency {
  statistic: ItemStatistic;
  value: number;
  /** Sample SD on the same scale. Absent below n = 2, and for `p_value`. */
  sd?: number;
  scaleMin?: number;
  scaleMax?: number;
}

export interface ItemOptionStat {
  optionId: string;
  /** Chosen this often. For `ranking`, ranked FIRST this often. */
  count: number;
  /** `ranking` only — the mean 1-based position the option was placed in. */
  meanRank?: number;
}

/** Time-on-item as percentiles, because latency is heavily right-skewed. */
export interface ItemTiming {
  n: number;
  p25Ms: number;
  medianMs: number;
  p75Ms: number;
}

/**
 * A statistic this item TYPE has no defensible definition for — `NotMeaningful`
 * in the contract.
 *
 * PERMANENT, and that is what separates it from the other two ways a number is
 * missing here: `sufficient: false` is fixed by more data, plain absence means
 * the data had no variance, and this one will never be filled however many
 * candidates sit the item. Rendering all three as one blank cell is the failure
 * the contract exists to prevent.
 */
export type NotMeaningful = 'central' | 'item_total_correlation' | 'option_distribution';

export interface ItemAnalysisRow {
  itemId: string;
  /** From the FROZEN copy — the type the item had when it was administered. */
  itemType: string;
  delivered: number;
  answered: number;
  /** `answered >= minimumN`. False ⇒ the estimates are withheld, not the counts. */
  sufficient: boolean;
  notMeaningful: NotMeaningful[];
  central?: ItemCentralTendency;
  /** CORRECTED item-total correlation — against the rest of the run, not the whole. */
  itemTotalCorrelation?: number;
  options?: ItemOptionStat[];
  timing?: ItemTiming;
  qcHeldRuns: number;
  /**
   * `qcHeldRuns / delivered`. **OMITTED, not zero, when held runs were excluded**
   * from the population — excluding them makes the rate 0 by construction, and a
   * console printing that 0 would report a cleaned analysis as a clean bank.
   */
  qcHoldRate?: number;
}

export interface ItemAnalysis {
  moduleId: string;
  personaId?: string;
  from?: string;
  to?: string;
  includeHeld: boolean;
  /** Published so a screen can say "24 of 30" rather than blanking a cell. */
  minimumN: number;
  runs: number;
  items: ItemAnalysisRow[];
  asOf: string;
}

/* ------------------------------------- V1: QC evidence (qcEvidence.ts) */

/** An option as the reviewer sees it: id and label, and no scoring key. */
export interface QcEvidenceOption {
  optionId: string;
  label: Localized;
}

/**
 * One item and its answer, **in delivery order** — `QcResponsePoint`.
 *
 * Delivery order is the whole point: a run that drifted into one value halfway
 * through looks nothing like one that started there, and any other sort destroys
 * exactly that distinction.
 */
export interface QcResponsePoint {
  position: number;
  itemId: string;
  itemType: string;
  /**
   * From the FROZEN copy — the prompt as administered. Rendered in the run's
   * `administeredLocale`, not the operator's: this is the harness exception
   * (CLAUDE.md §28c), because the reviewer is judging what the candidate met.
   */
  prompt: Localized;
  options?: QcEvidenceOption[];
  /** Distinguishes "did not answer" from "answered 0", real on a 0–100 slider. */
  answered: boolean;
  /** Absent when unanswered — never defaulted, and never drawn as a zero. */
  value?: number | string | string[];
  responseTimeMs?: number;
  /**
   * Decided SERVER-SIDE, so the comparison is made once. Absent when the item
   * was untimed, and absent for EVERY item when the pinned config could not be
   * resolved. Never re-derive it here — that would put the rule in two places.
   */
  belowThreshold?: boolean;
  /** Whether this item is in the straight-lining denominator. */
  scalar: boolean;
  scaleMin?: number;
  scaleMax?: number;
  /** A scoring key, and here it IS the evidence — see `contract/qcEvidence.ts`. */
  dimension?: string;
  reverseKeyed?: boolean;
}

export interface StraightLiningEvidence {
  /** Scalar items answered with a NUMBER — the denominator, not the run length. */
  n: number;
  minimumN: number;
  dominantValue?: number;
  dominantCount?: number;
  proportion?: number;
  threshold: number;
  flagged: boolean;
}

export interface SpeedingEvidence {
  n: number;
  /** The MEDIAN, because that is the number the rule reads. */
  medianMs?: number;
  /**
   * DESCRIPTIVE ONLY. It is not what fires the flag: a run with 9 of 20 items
   * under the threshold and a median above it is not `speeding`, and presenting
   * this as the trigger would invent a second rule.
   */
  belowThreshold: number;
  thresholdMs: number;
  flagged: boolean;
}

export interface QcDetectorEvidence {
  straightLining: StraightLiningEvidence;
  speeding: SpeedingEvidence;
  /** The same rule re-run over the same inputs — compare with `qcFlags`. */
  flags: string[];
}

export interface QcEvidence {
  resultId: string;
  assessmentId: string;
  candidateRef: string;
  moduleId: string;
  personaId: string;
  state: string;
  administeredLocale: string;
  /** The flags STORED on the envelope — the immutable half of the record. */
  qcFlags: string[];
  qcReview?: QcReviewRecord;
  configVersion: number;
  instrumentVersion: string;
  /**
   * Whether the config version this run was scored under is still retained.
   *
   * `false` ⇒ `detectors` is absent and no point carries `belowThreshold`. The
   * screen must SAY that rather than substituting today's thresholds: a plausible
   * number beside flags produced by a different rule reads as an answer.
   */
  configResolved: boolean;
  detectors?: QcDetectorEvidence;
  itemCount: number;
  answeredCount: number;
  /** Guardrail G2, published so a chart can be sized without a second copy of it. */
  maxItems: number;
  pattern: QcResponsePoint[];
  scoredAt?: string;
  finalizedAt: string;
  asOf: string;
}

export interface ImportReport {
  dryRun: boolean;
  wouldCreate: number;
  wouldUpdate: number;
  created: number;
  updated: number;
  errors: Array<{ row: number; message: string; path?: string }>;
  /** What each row would do, before anything does it. */
  rows: Array<{ row: number; itemId?: string; action: 'create' | 'update' | 'error' }>;
  /** CSV only — the mapping step. `unmapped` is what the operator must resolve. */
  headers: string[];
  unmapped: string[];
  knownColumns: string[];
}

export interface AuditRow {
  eventId: string;
  /** Position in the hash chain, from 1. */
  seq: number;
  ts: string;
  actor: string;
  category: 'Auth' | 'Design' | 'Delivery' | 'Scoring' | 'Integration' | 'System';
  event: string;
  entityType: string;
  entityId: string;
  summary: string;
  diff?: Record<string, unknown>;
  ip?: string;
  traceId: string;
  severity: 'info' | 'notice' | 'warning' | 'critical';
  prevHash: string;
  hash: string;
}

export interface AuditVerification {
  ok: boolean;
  checked: number;
  brokenAt?: number;
  reason?: string;
}

export interface CatalogueBundle {
  schemaVersion: number;
  exportedAt: string;
  source: string;
  modules: unknown[];
  personas: unknown[];
  matrixCells: unknown[];
  questions?: unknown[];
}

export interface CatalogueImportReport {
  dryRun: boolean;
  source: string;
  summary: { create: number; update: number; skip: number; conflict: number };
  plan: Array<{
    kind: 'module' | 'persona' | 'matrixCell' | 'question';
    id: string;
    action: 'create' | 'update' | 'skip' | 'conflict';
    detail?: string;
  }>;
  applied: Record<string, number> | null;
}
